import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Cookie, Database, Mail, Globe } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-muted-foreground">Last updated: February 2026</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              ScanIt2Go ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our NFC tag management platform and related services.
            </p>
          </section>

          {/* Data Collection */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Information We Collect
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Information You Provide</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Messages and text content you create for tags</li>
                  <li>Photos and videos you upload</li>
                  <li>Email address (if you opt-in for notifications)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Information Collected Automatically</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>IP address (for approximate location)</li>
                  <li>Device type and browser information</li>
                  <li>Date and time of access</li>
                  <li>Pages and features used</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">How We Use Your Information</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Service Delivery:</strong> Displaying your content on NFC tags and QR codes</li>
              <li><strong>Analytics:</strong> Understanding how tags are used</li>
              <li><strong>Notifications:</strong> Sending scan alerts (with your consent)</li>
              <li><strong>Security:</strong> Preventing fraud and unauthorized access</li>
              <li><strong>Improvements:</strong> Enhancing our platform</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              Cookies
            </h2>
            <p className="text-muted-foreground mb-4">
              We use cookies to maintain your session, remember your preferences, and analyze usage. 
              You can manage your cookie preferences at any time.
            </p>
            <div className="bg-secondary/50 rounded-xl p-4">
              <h3 className="font-medium mb-2">Cookie Types:</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li><strong>Essential:</strong> Required for site functionality</li>
                <li><strong>Functional:</strong> Remember your preferences</li>
                <li><strong>Analytics:</strong> Help us understand usage (with consent)</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Your Rights</h2>
            <p className="text-muted-foreground mb-4">
              Under GDPR and UK GDPR, you have the right to:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { right: 'Access', desc: 'Request a copy of your data' },
                { right: 'Rectification', desc: 'Correct inaccurate data' },
                { right: 'Erasure', desc: 'Request deletion of your data' },
                { right: 'Portability', desc: 'Receive data in portable format' },
                { right: 'Object', desc: 'Object to certain processing' },
                { right: 'Withdraw Consent', desc: 'Withdraw consent anytime' }
              ].map((item) => (
                <div key={item.right} className="bg-secondary/50 rounded-lg p-3">
                  <p className="font-medium text-sm">{item.right}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Data Retention */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 font-medium">Data Type</th>
                    <th className="text-left py-2 font-medium">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-2">Tag content</td>
                    <td className="py-2">Until you delete it</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2">Analytics data</td>
                    <td className="py-2">24 months</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2">Account information</td>
                    <td className="py-2">Until account closure + 30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-muted-foreground mb-4">
              For privacy-related inquiries or to exercise your rights:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p><strong>Email:</strong> privacy@scanit2go.com</p>
              <p><strong>General Contact:</strong> andy@scanit2go.com</p>
              <p><strong>Phone:</strong> +44 7829 710 543</p>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* Footer */}
          <div className="text-center text-sm text-muted-foreground pt-8 border-t border-border">
            <p>© 2026 ScanIt2Go. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
              <span>•</span>
              <a href="https://app.scanit2go.com" className="hover:text-primary flex items-center gap-1">
                <Globe className="w-3 h-3" />
                scanit2go.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
